import React, { useState } from 'react'
import {
  useDispatch,
  useSelector,
} from 'react-redux'

import {
  Geocode,
  getGeocode,
  GoogleMapsContextProvider,
} from 'utils/apis/googleGeocoding'

import { RootState } from 'state/types'

import {
  setCurrentQuoteRequestEntries,
  setCurrentQuoteRequestEntry,
} from 'state/quotes/actions'

import { getQuoteRequestValue } from 'state/quotes/selectors'

import { Value } from 'components/base/Form/Input/types'

import {
  ContainerProps,
  OnValueChangeHandler,
  SetIsSuggestionSelected,
  StoreGeocode,
} from './types'

import Address from './index.presentational'

const fetchGeocode = async (value: Value = undefined): Promise<Geocode | undefined> => {
  if (!value) {
    return undefined
  }

  const geocode = await getGeocode(value as string)

  return geocode
}

const AddressContainer: React.FC<ContainerProps> = ({
  alwaysShowError = false,
  loading: loadingProp = false,
  onBlur: onBlurProp = (): void => undefined,
  onChange: onChangeProp = (): void => undefined,
  onSelect: onSelectProp = (): void => undefined,
  quoteRequestField,
  storeGeocodeTo,
  value,
  ...props
}): React.ReactElement => {
  const dispatch = useDispatch()

  const [
    loading,
    setLoading,
  ] = useState(loadingProp)

  const [
    isSuggestionSelectedLocalState,
    setIsSuggestionSelectedLocalState,
  ] = useState(false)

  const reduxIsSuggestionSelectedKey = `${quoteRequestField}-isSuggestionSelected`

  const isSuggestionSelectedReduxState = useSelector((state: RootState) => (quoteRequestField && getQuoteRequestValue(state, reduxIsSuggestionSelectedKey)) || undefined)

  const isSuggestionSelected = quoteRequestField ? isSuggestionSelectedReduxState : isSuggestionSelectedLocalState

  const setIsSuggestionSelected: SetIsSuggestionSelected = value => {
    if (quoteRequestField) {
      dispatch(setCurrentQuoteRequestEntry(reduxIsSuggestionSelectedKey, value))
    }
    else {
      setIsSuggestionSelectedLocalState(value)
    }
  }

  const shouldFetchGeocode = (storeGeocodeTo && true) || false

  const onChange: OnValueChangeHandler = value => {
    setIsSuggestionSelected(false)

    onChangeProp({ target: { value } } as unknown as React.ChangeEvent<HTMLInputElement>)
  }

  const onBlur: OnValueChangeHandler = async value => {
    onBlurProp && onBlurProp({ target: { value } })
  }

  const onSelect: OnValueChangeHandler = async value => {
    setIsSuggestionSelected(true)

    if (storeGeocodeTo) {
      storeGeocode(storeGeocodeTo, {
        data: {
          isLoading: true,
          isValid: false,
        },
      })
    }

    onChangeProp({ target: { value } } as unknown as React.ChangeEvent<HTMLInputElement>)

    let geocode
    if (shouldFetchGeocode) {
      setLoading(true)

      geocode = await fetchGeocode(value as Value)

      setLoading(loadingProp)
    }

    if (storeGeocodeTo) {
      storeGeocode(storeGeocodeTo, geocode)
    }

    onSelectProp({ target: { value } } as unknown as React.ChangeEvent<HTMLInputElement>, geocode)
  }

  const storeGeocode: StoreGeocode = (quoteRequestKey, geocode) => {
    let quoteRequestEntries = {}

    if (geocode?.data) {
      quoteRequestEntries = Object.keys(geocode.data).reduce((acc, key) => {
        acc[`${quoteRequestKey}.${key}`] = geocode.data[key]

        return acc
      }, {})
    }
    else{
      // Clear values
      quoteRequestEntries = [
        'isValid',
        'streetNumber',
        'streetName',
        'city',
        'county',
        'stateAbbr',
        'zip5',
        'zip4',
        'zipCode',
      ].reduce((acc, key) => {
        acc[`${quoteRequestKey}.${key}`] = undefined

        return acc
      }, {})
    }

    dispatch(setCurrentQuoteRequestEntries(quoteRequestEntries))
  }

  return React.createElement(
    GoogleMapsContextProvider,
    null,
    React.createElement(Address, {
      ...props,
      isSuggestionSelected,
      loading,
      onBlur,
      onChange,
      onSelect,
      touched: props.touched || alwaysShowError,
      valid: !!value && !alwaysShowError,
      value,
    }),
  )
}

export * from './types'

export default AddressContainer
